import { clsx } from 'clsx'
import { Button } from 'component/Button'
import { IconButton } from 'component/Button/IconButton'
import { CarouselStore } from 'component/Carousel/store/CarouselStore'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { Actions } from 'page/InferenceScreen/Actions'
import { Image } from 'page/InferenceScreen/Carousel/Image'
import { Video } from 'page/InferenceScreen/Carousel/Video'
import { imageQrModal, ImageQrModalStore } from 'page/InferenceScreen/ImageQrModal/ImageQrModalStore'
import { LikeActions } from 'page/InferenceScreen/LikeActions'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import { ProcessingCard } from 'shared/inference/ProcessingCard'
import { InferenceStore } from 'store/inference/InferenceStore'
import { opt } from 'store/opt'
import { store } from 'store/store'
import { formatDate } from 'util/date'
import { Size } from 'util/size'
import { disk, space } from 'util/typo'
import css from './Card2.module.scss'

type Props = {
  carousel: CarouselStore<InferenceStore>
  inference: InferenceStore
  onClick?: (active: boolean) => void
}

const Failed = observer(() => {
  return (
    <Vertical className={css.error} wide tall center>
      <Icon name="error" size={opt.stand ? 64 : 48} error />
    </Vertical>
  )
})

const Done = observer(({ carousel, inference }: Props) => {
  const { tablet } = store.frame
  const { item } = carousel
  const { created_at } = inference
  const source = inference.sources[0]
  const video = inference.videos[0]?.src
  const is_video = !!video
  const active = inference === item && carousel.stable
  const actions = !opt.stand && inference.complete
  const empty = !video && !source

  const videoRef = useRef<HTMLVideoElement>(null)
  const [goodVideo, setGoodVideo] = useState(false)
  const [, setSizeVideo] = useState<Size>()
  const [goodImage, setGoodImage] = useState(false)
  const [, setSizeImage] = useState<Size>()

  const [touch, setTouch] = useState(false)
  const [rewind, setRewind] = useState(false)
  const [play, setPlay] = useState(false)

  const className = clsx(css.card, !goodVideo && !goodImage && css.blank)

  useEffectOnce(() => {
    const element = videoRef.current
    if (!element) return

    element.load()
    element.addEventListener('play', () => {
      setTouch(true)
      setRewind(false)
      setPlay(true)
    })
    element.addEventListener('pause', () => setPlay(false))
    element.addEventListener('ended', () => {
      element.currentTime = 0
      setRewind(true)
    })
  })

  useEffect(() => {
    if (!active) {
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.currentTime = 0
      }
      setTouch(false)
      setRewind(false)
      setPlay(false)
    }
  }, [active])

  const onImageRef = useLatestCallback(
    (element: HTMLImageElement | undefined | null) => {
      inference.image_element = element || undefined
    },
  )

  const onButton = useLatestCallback((event: MouseEvent) => {
    event.stopPropagation()
  })

  const onPlay = useLatestCallback(() => {
    setTimeout(() => {
      if (!videoRef.current) return
      if (play) void videoRef.current.pause()
      else void videoRef.current.play()
    }, 50)
  })

  const onShare = useLatestCallback(() => {
    if (item) imageQrModal.open(new ImageQrModalStore(item))
  })

  if (empty) return <Vertical className={clsx(css.card, css.blank)}></Vertical>

  return (
    <div className={className}>
      {is_video && (
        <Video
          videoRef={videoRef}
          inference={inference}
          onGood={setGoodVideo}
          onSize={setSizeVideo}
        />
      )}
      {(!is_video || !touch) && (
        <Image
          imageRef={onImageRef}
          inference={inference}
          preview={is_video && goodVideo}
          onGood={setGoodImage}
          onSize={setSizeImage}
        />
      )}

      {(actions || is_video) && <div
        className={clsx(css.actions, active && css.active)}
        onClick={onButton}
      >
        {actions && (
          <Vertical pin={[8, 8, null, null]}>
            <Actions inference={inference} />
          </Vertical>
        )}
        {actions && opt.likes && (
          <Vertical pin={[null, 8, 8, null]}>
            <LikeActions inference={inference} />
          </Vertical>
        )}
        {is_video && (
          <Space gap={8} pin={[0, 0, 0, 0]} center>
            <IconButton
              className={clsx(css.play, play && css.busy)}
              action={onPlay}
            >
              <Icon
                size={24}
                fill
                name={rewind ? 'refresh' : play ? 'pause' : 'play_arrow'}
              />
            </IconButton>
          </Space>
        )}
      </div>}
      {opt.boutique && <Space pin={[null, null, 8, 8]} className={clsx(css.date, active && css.active)}>
        <Typo size={11} one secondary>
          {formatDate(created_at, 'DD.MM.YYYY')}
          {space}
          {disk}
          {space}
          {formatDate(created_at, 'HH:mm')}
        </Typo>
      </Space>}
      {opt.stand2 && tablet && active && <Space pin={[null, 12, 12, null]}>
        <Button size={64} compact action={onShare}>
          <Icon size={24} name="ios_share" />
        </Button>
      </Space>}
    </div>
  )
})

export const Card2 = observer((props: Props) => {
  const { carousel, inference, onClick } = props
  const { error, complete } = inference
  const className = clsx(css.root, opt.basic && css.basic)

  let content
  if (error) content = <Failed />
  else if (!complete) content = <ProcessingCard inference={inference} animated />
  else content = <Done {...props} />

  const onClickInner = useLatestCallback(() => {
    const active = carousel.item === inference
    carousel.item = inference
    if (onClick) onClick(active)
  })

  return <Space className={className} top onClick={onClickInner}>
    {content}
  </Space>
})
